import {MdFrameSpec, primaryDoorsItem} from "../../model/frame/MdFrameSpec";
import {FrmVar} from "../../drawfrm/FrmVar";
import {frameOuterRect} from "./FrameOuterDimension";
import {TFrameBlock} from "./FrameBlockExt";
import {FrameBlockItem} from "./FrameBlockItem";
import {DimEntry, DimensionStack} from "../export/DimensionStack";
import {getSwingSide} from "../frameProfile/Helpers";
import type {FrameGridView} from "./FrameGridScene";

// the door's **nominal** sizes, drawn outside the frame — widths across the top, height
// down the left — and always visible in the interactive frame grid.
//
// Nominal, not actual: these are the three opening columns (`nominalWidth`,
// `inactiveDoorWidth`, `nominalHeight`), so no undercut, overcut, top/bottom gap,
// jamb/stop gap or pair gap enters any of them. That's what makes them editable — each
// one drives its own source FrmVar on MdFrameSpec, and a save writes them back to the
// opening (see ElevationApp.nominalDimensions). The actual leaf sizes stay where they
// were: derived, and dimensioned by the export stack.
//
// The spans are exact by construction rather than by measuring the drawing:
// `nominalWidth + inActiveDoorWidth == openingWidth == innerWidth == the doors cell's
// width`, so on a pair the two width dimensions tile that cell; and the doors cell's top
// plus `nominalHeight` is the true floor (the cell stops at the threshold, and the frame
// is cut down by undercutFrame above the floor — see MdFrameSpec's door height).
export class DoorNominalDimensions {
    private view: FrameGridView;
    private stack: DimensionStack;

    constructor(view: FrameGridView) {
        this.view = view;
        this.stack = new DimensionStack(view.elev.draw);
    }

    draw(root: TFrameBlock, spec: MdFrameSpec) {
        // in external-dimensions mode (the screen export) every measurement is drawn by
        // ExportFrameDimensions instead — read-only and including the actual sizes.
        if(this.view.elev.viewSettings.externalDimensions.value) {
            this.stack.hide();
            return;
        }

        const outer = frameOuterRect(root, spec);
        // the doors cell's width IS openingWidth (wireDoorsItem binds it to innerWidth), so
        // the width dimensions tile it exactly. A frame with no door cell falls back to the
        // root's content rect — the same clear opening, just without a leaf in it.
        const primary = primaryDoorsItem(spec);
        const door = (root.getItems() as FrameBlockItem[]).find(c => c.model === primary);
        const rect = door?.layoutRect ?? root.layoutRect;
        if(!outer || !rect) {
            this.stack.hide();
            return;
        }

        this.stack.draw({
            rect: outer,
            // re-attach to the current lines layer every draw: a grid rebuild swaps
            // parentThing.lines for a fresh Graphics, orphaning our container.
            parent: this.view.parentThing.lines,
            top: this.widthEntries(spec, rect),
            left: [this.heightEntry(spec, rect)],
        });
    }

    // the width dimension(s) across the doors cell: one for a single opening, and for a
    // pair the active leaf's nominal share plus the inactive leaf's, laid out in the order
    // the leaves are drawn (see DoorFace.draw).
    private widthEntries(spec: MdFrameSpec, rect: {x: number; width: number}): DimEntry[] {
        const nominal = this.entry(rect.x, spec.nominalWidth.get(), spec.nominalWidth, "nominal-width-dim-edit");
        if(!spec.isPair) return [nominal];

        const inactiveWidth = spec.inActiveDoorWidth.get();
        const activeIsOnLeft = getSwingSide(this.view.elev, true).side === "left";

        if(activeIsOnLeft) {
            return [
                nominal,
                this.entry(rect.x + spec.nominalWidth.get(), inactiveWidth, spec.inActiveDoorWidth, "inactive-width-dim-edit"),
            ];
        }

        return [
            this.entry(rect.x, inactiveWidth, spec.inActiveDoorWidth, "inactive-width-dim-edit"),
            this.entry(rect.x + inactiveWidth, spec.nominalWidth.get(), spec.nominalWidth, "nominal-width-dim-edit"),
        ];
    }

    // the opening's nominal height: the doors cell's top down to the floor.
    private heightEntry(spec: MdFrameSpec, rect: {y: number}): DimEntry {
        return this.entry(rect.y, spec.nominalHeight.get(), spec.nominalHeight, "nominal-height-dim-edit");
    }

    // one editable span starting at `lo` and running the var's current value.
    private entry(lo: number, length: number, v: FrmVar, debug: string): DimEntry {
        return {
            lo,
            hi: lo + length,
            value: length,
            lane: 0,
            modified: v.isModified(),
            onSet: next => {
                v.userSet(next, {debug});
                this.relayout();
            },
            onClear: () => {
                v.userClearModified();
                this.relayout();
            },
        };
    }

    private relayout() {
        this.view.render();
        this.view.parentThing.changed.emit("re-layout");
    }

    hide() {
        this.stack.hide();
    }

    destroy() {
        this.stack.destroy();
    }
}
